/**
 * Which use cases run on the server in live mode, and how the server authorises them. Pure data shared by the browser proxy
 * (usecases.ts) and the Edge Functions (synced copy).
 *
 *   public  : callable without a login. The use case itself checks the secret token it is given (manage token, offer token, OTP).
 *   owner   : needs a signed-in member of the business the arguments point at (`business` says where to find it).
 *   either  : a client with a token OR an owner (cancel / move): `by` in the input says which.
 *
 * Everything not listed here (reads and plain edits of the owner's own rows) runs in the browser against RLS-protected tables.
 */
export type OpAccess = 'public' | 'owner' | 'either';
/** Where the Edge Function finds the business an owner op targets. */
export type BusinessRef = 'none' | 'arg0' | 'arg0.businessId' | 'arg0.appointmentId' | 'appointment0' | 'client0';
export interface OpSpec { fn: string; access: OpAccess; business?: BusinessRef }

export const OPS = {
  // public booking surface
  getBookingPage: { fn: 'app', access: 'public' },
  getSlots: { fn: 'app', access: 'public' },
  resolveSlug: { fn: 'app', access: 'public' },
  staffForService: { fn: 'app', access: 'public' },
  bookAsClient: { fn: 'book', access: 'public' },
  getManageView: { fn: 'app', access: 'public' },
  requestBookingLookup: { fn: 'app', access: 'public' },
  lookupBookingsByPhone: { fn: 'app', access: 'public' },
  claimDeposit: { fn: 'claim_deposit', access: 'public' },
  joinWaitlist: { fn: 'app', access: 'public' },
  leaveWaitlist: { fn: 'app', access: 'public' },
  getOfferView: { fn: 'app', access: 'public' },
  acceptOffer: { fn: 'app', access: 'public' },
  declineOffer: { fn: 'app', access: 'public' },
  previewMyDataErasure: { fn: 'app', access: 'public' },
  eraseMyData: { fn: 'app', access: 'public' },
  // client (token) or owner
  moveAppointment: { fn: 'move', access: 'either', business: 'arg0.appointmentId' },
  cancelAppointment: { fn: 'cancel', access: 'either', business: 'arg0.appointmentId' },
  // owner operations that create tokens, send messages or touch money
  createOwnerAccount: { fn: 'app', access: 'owner', business: 'none' },
  bookAsOwner: { fn: 'book', access: 'owner', business: 'arg0.businessId' },
  undoCancel: { fn: 'cancel', access: 'owner', business: 'appointment0' },
  notifyRescheduled: { fn: 'app', access: 'owner', business: 'arg0.appointmentId' },
  completeAppointment: { fn: 'app', access: 'owner', business: 'appointment0' },
  markNoShow: { fn: 'app', access: 'owner', business: 'appointment0' },
  markPaid: { fn: 'app', access: 'owner', business: 'appointment0' },
  confirmDepositReceived: { fn: 'confirm_deposit', access: 'owner', business: 'appointment0' },
  rejectDepositClaim: { fn: 'confirm_deposit', access: 'owner', business: 'appointment0' },
  offerSlotToWaitlist: { fn: 'app', access: 'owner', business: 'arg0.businessId' },
  deleteClientData: { fn: 'app', access: 'owner', business: 'client0' },
  importClientsCsv: { fn: 'app', access: 'owner', business: 'arg0' },
  previewAccountDeletion: { fn: 'delete_account', access: 'owner', business: 'arg0' },
  deleteOwnerAccount: { fn: 'delete_account', access: 'owner', business: 'arg0.businessId' },
  cancelOwnerDeletion: { fn: 'delete_account', access: 'owner', business: 'arg0' },
} as const satisfies Record<string, OpSpec>;
export type RemoteOp = keyof typeof OPS;
export const isRemoteOp = (op: string): op is RemoteOp => Object.prototype.hasOwnProperty.call(OPS, op);

/** Functions that exist as Edge Functions, and the ops each one may run. `app` is the generic dispatcher. */
export const FUNCTION_OPS: Record<string, RemoteOp[]> = (Object.entries(OPS) as Array<[RemoteOp, OpSpec]>).reduce<Record<string, RemoteOp[]>>((acc, [op, spec]) => {
  (acc[spec.fn] ??= []).push(op);
  return acc;
}, {});

/** Request/response envelope of every use-case Edge Function. The response body is the use case's own Result, unchanged. */
export interface OpRequest { op: string; args: unknown[] }
